import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Heart, Code, GraduationCap, DollarSign, Home, CheckCircle } from 'lucide-react';

const useCases = [
  {
    id: 'ecommerce',
    label: 'E-commerce',
    icon: ShoppingCart,
    title: 'Boost Sales & Support',
    description: 'Automate customer inquiries, order tracking, and product recommendations to increase conversions and reduce cart abandonment.',
    features: [
      'Product recommendations and upselling',
      'Order status and tracking updates',
      'Shopping cart recovery',
      'Returns and refund processing',
      'Inventory and availability checks',
    ],
  },
  {
    id: 'healthcare',
    label: 'Healthcare',
    icon: Heart,
    title: 'Patient Care & Scheduling',
    description: 'Streamline appointment booking, answer medical FAQs, and provide 24/7 patient support while maintaining HIPAA compliance.',
    features: [
      'Appointment scheduling and reminders',
      'Medical FAQ and symptom information',
      'Prescription refill requests',
      'Insurance verification support',
      'Post-visit follow-ups',
    ],
  },
  {
    id: 'saas',
    label: 'SaaS',
    icon: Code,
    title: 'Scale Customer Success',
    description: 'Guide users through onboarding, answer technical questions, and reduce support tickets while your team focuses on high-value interactions.',
    features: [
      'Interactive product onboarding',
      'Feature tutorials and documentation',
      'Technical troubleshooting',
      'Account and billing inquiries',
      'Feature request collection',
    ],
  },
  {
    id: 'education',
    label: 'Education',
    icon: GraduationCap,
    title: 'Support Students 24/7',
    description: 'Help students with course information, assignment deadlines, and campus resources anytime they need assistance.',
    features: [
      'Course enrollment and schedules',
      'Assignment and deadline reminders',
      'Campus resource information',
      'Academic support referrals',
      'Event and activity updates',
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: DollarSign,
    title: 'Secure Banking Support',
    description: 'Handle account inquiries, transaction alerts, and basic banking tasks with bank-grade security and compliance.',
    features: [
      'Account balance and transactions',
      'Payment and transfer assistance',
      'Fraud alert notifications',
      'Credit card and loan information',
      'Branch and ATM locator',
    ],
  },
  {
    id: 'realestate',
    label: 'Real Estate',
    icon: Home,
    title: 'Qualify & Convert Leads',
    description: 'Engage prospects 24/7, schedule property viewings, and provide instant answers to property questions.',
    features: [
      'Property search and filtering',
      'Viewing appointment scheduling',
      'Neighborhood and amenity information',
      'Mortgage calculator and pre-qualification',
      'Lead qualification and routing',
    ],
  },
];

export const UseCases: React.FC = () => {
  const [activeCase, setActiveCase] = useState('ecommerce');
  const currentCase = useCases.find((uc) => uc.id === activeCase) || useCases[0];
  const Icon = currentCase.icon;

  return (
    <section id="use-cases" className="relative py-24">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Built for <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Every Industry</span>
          </h2>
          <p className="text-xl text-gray-400">No matter your industry, Looplet AI adapts to your needs</p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {useCases.map((useCase) => {
            const UseCaseIcon = useCase.icon;
            return (
              <button
                key={useCase.id}
                onClick={() => setActiveCase(useCase.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeCase === useCase.id
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <UseCaseIcon size={18} />
                {useCase.label}
              </button>
            );
          })}
        </div>

        <motion.div
          key={activeCase}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-2xl p-8 md:p-12"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left side - Description */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                  <Icon className="text-white" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-white">{currentCase.title}</h3>
              </div>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                {currentCase.description}
              </p>
              <motion.a
                href="/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold"
              >
                Get Started
              </motion.a>
            </div>

            {/* Right side - Features */}
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
              <h4 className="text-xl font-semibold text-white mb-4">Key Capabilities</h4>
              <ul className="space-y-3">
                {currentCase.features.map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-300">{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
